import { getFeedback, setContext, setTag } from '@sentry/gatsby'
import React, { useCallback, useEffect, useRef, useState } from 'react'

import useIsMounted from '../hooks/useIsMounted'

import DFHLogo from '../components/buy/checkout/images/DFH_logo.svg'

// How many times the app-level boundary has shown recovery this page load. A count above 1 means the
// failure recurred after a recovery attempt, which is exactly what we want to see in Sentry.
let recoveryActivations = 0

type FeedbackForm = Awaited<ReturnType<NonNullable<ReturnType<typeof getFeedback>>['createForm']>>

/**
 * Why the app-level boundary stopped re-mounting: a render loop, a DOM operation broken by a browser
 * translator or extension, or any other error that recurred after the one re-mount.
 */
export type RecoveryReason = 'render-loop' | 'dom-mutation' | 'remount-failed'

interface RenderLoopRecoveryFallbackProps {
  /** Sentry event id of the caught error, so submitted feedback can be correlated to the crash. */
  eventId?: string
  reason: RecoveryReason
}

/**
 * Last-resort recovery UI shown when the app-level error boundary catches an error that re-mounting
 * would only repeat (see `RecoveryReason`). It renders in place of the entire app subtree, so it
 * lives OUTSIDE every provider (Auth0/Apollo/theme/cart). It therefore uses only global Tailwind
 * styles, a build-time logo import, and the Sentry SDK global (a singleton, not React context).
 * Reloading or leaving checkout gets past a render loop; a translator's DOM error needs translation
 * turned off as well, which the copy says. The cart persists in storage, so no cart data is lost.
 */
export const RenderLoopRecoveryFallback: React.FC<RenderLoopRecoveryFallbackProps> = ({
  eventId,
  reason,
}) => {
  const feedback = typeof window !== 'undefined' ? getFeedback() : undefined
  const formRef = useRef<FeedbackForm | null>(null)
  const [busy, setBusy] = useState(false)
  const isMounted = useIsMounted()

  // The feedback dialog is appended to Sentry's shadow DOM, outside React's tree, so unmounting this
  // component would otherwise leave it behind — and a later mount would append a second one, since
  // the SDK's append guard only compares against its own instance. `removeFromDom` also restores the
  // body overflow that `open` sets, covering an unmount while the form is still open. Today both
  // exits from this UI are full page loads, so this is insurance rather than an observed leak.
  useEffect(
    () => () => {
      formRef.current?.removeFromDom()
      formRef.current = null
    },
    [],
  )

  // Mark that the recovery boundary activated, so any follow-up event (e.g. submitted feedback) is
  // tagged and the activation count is visible. The original error already sent before this UI
  // mounted; this correlates what happened next.
  useEffect(() => {
    recoveryActivations += 1
    if (reason === 'render-loop') {
      setTag('render_loop.recovered', 'true')
    }
    setTag('app_recovery.reason', reason)
    setContext('Render Loop Recovery', {
      activated: true,
      activation_count: recoveryActivations,
      path: typeof window !== 'undefined' ? window.location.pathname : undefined,
      reason,
    })
  }, [reason])

  // The boundary is app-wide, so tailor the copy to where the loop actually fired.
  const onCheckout =
    typeof window !== 'undefined' && /\/checkout(?:\/|$)/.test(window.location.pathname)
  const description = onCheckout
    ? "We couldn't finish loading checkout. Your cart is saved, so you can refresh and try again."
    : "We couldn't finish loading this page. Please refresh to try again."

  const openFeedback = useCallback(async () => {
    if (!feedback) {
      return
    }
    try {
      setBusy(true)
      if (!formRef.current) {
        const form = await feedback.createForm({
          ...(eventId ? { tags: { associated_event_id: eventId } } : {}),
        })
        // Unmounting during the await already ran the cleanup above, and it saw an empty ref.
        // Appending now would strand a dialog in the shadow DOM that nothing will ever remove.
        // Dropping the form without appending leaves nothing behind: only `appendToDom` adds to it.
        if (!isMounted.current) {
          return
        }
        formRef.current = form
        formRef.current.appendToDom()
      }
      formRef.current.open()
    } catch {
      // The feedback form is best-effort and non-critical to recovery; ignore failures to open it.
    } finally {
      // createForm is awaited, so the component can unmount mid-flight.
      if (isMounted.current) {
        setBusy(false)
      }
    }
  }, [feedback, eventId, isMounted])

  return (
    <div
      role="alert"
      className="flex min-h-screen flex-col items-center justify-center gap-6 p-8 text-center"
    >
      <img src={DFHLogo} alt="Designs for Health" className="h-9 w-auto md:h-10" />

      <div className="flex max-w-md flex-col gap-2">
        <h1 className="text-2xl font-bold text-brand-navy">Oops! Something went wrong</h1>
        <p className="text-brand-navy">{description}</p>
        {/* Refreshing alone repeats the crash, because the browser translates the page again. */}
        {reason === 'dom-mutation' && (
          <p className="text-brand-navy">
            If your browser is translating this page, turn translation off and refresh.
          </p>
        )}
      </div>

      <div className="flex flex-wrap items-center justify-center gap-3">
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="rounded-md bg-brand-navy px-5 py-2.5 font-semibold text-white transition-opacity hover:opacity-90"
        >
          Refresh
        </button>
        {/* "Return Home" links to /products, matching the site convention (see Confirmation). */}
        <a
          href="/products"
          className="rounded-md border border-brand-navy px-5 py-2.5 font-semibold text-brand-navy transition-colors hover:bg-brand-navy hover:text-white"
        >
          Return Home
        </a>
      </div>

      {feedback && (
        <button
          type="button"
          onClick={openFeedback}
          disabled={busy}
          className="text-sm font-semibold text-brand-orange underline underline-offset-2 disabled:opacity-60"
        >
          Tell us what happened
        </button>
      )}
    </div>
  )
}
